import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { RefreshCw, Download, ChevronDown, Loader2 } from 'lucide-react';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import { meApi, MyPhoto } from '../../api/me';
import { useAuthStore } from '../../store/authStore';
import { Photo } from '../../types';
import { Button } from '../UI/Button';
import { MachhaCharacter } from '../../lib/machhaAsset';
import { Skeleton, PhotoCardSkeleton } from '../UI/Skeleton';
import { AlbumPhotoCell } from '../Albums/AlbumPhotoCell';
import { AlbumPhotoViewer } from '../Albums/AlbumPhotoViewer';
import { ShareSheet } from '../Albums/ShareSheet';
import { PhotoReportSheet } from '../Albums/PhotoReportSheet';
import { PhotoGrid } from '../shared/PhotoGrid';
import { CLIENT_ZIP_ENABLED, startClientZip } from '../../services/download';
import styles from './MyPhotosTab.module.css';

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatRelativeTime(isoString: string | null): string {
  if (!isoString) return 'Never';
  const diff = Date.now() - new Date(isoString).getTime();
  const minutes = Math.floor(diff / 60_000);
  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

function myPhotoToViewerPhoto(p: MyPhoto): Photo {
  return {
    _id: p._id,
    eventId: p.albumId,
    ftpAccountId: null,
    originalFilename: p.originalFilename,
    storagePath: '',
    thumbnailPath: '',
    fileSizeBytes: 0,
    uploadedAt: p.uploadedAt,
    thumbnailUrl: p.thumbnailUrl,
    originalUrl: p.originalUrl,
    processedUrl: p.processedUrl,
  };
}

/** Per-album load state. Each album group fetches its own photos on demand. */
interface AlbumLoad {
  photos: MyPhoto[];
  cursor: string | null;
  hasMore: boolean;
  loading: boolean;
  loaded: boolean;
}

const EMPTY_LOAD: AlbumLoad = { photos: [], cursor: null, hasMore: false, loading: false, loaded: false };

/** Zip a set of photos into a downloadable archive under `folderName`. */
async function downloadPhotosAsZip(
  photos: MyPhoto[],
  folderName: string,
  zipName: string,
  toastId: string,
): Promise<void> {
  const JSZip = (await import('jszip')).default;
  const zip = new JSZip();
  const folder = zip.folder(folderName)!;

  let succeeded = 0;
  let failed = 0;

  for (let i = 0; i < photos.length; i += 4) {
    const batch = photos.slice(i, i + 4);
    const results = await Promise.allSettled(
      batch.map(async (photo) => {
        const url = photo.processedUrl || photo.originalUrl;
        if (!url) throw new Error('No URL');
        const r = await fetch(url);
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const blob = await r.blob();
        const ext = photo.processedUrl ? 'jpg' : photo.originalFilename.split('.').pop() ?? 'jpg';
        folder.file(photo.originalFilename.replace(/\.[^.]+$/, '') + '.' + ext, blob);
      }),
    );
    results.forEach((r) => (r.status === 'fulfilled' ? succeeded++ : failed++));
  }

  const blob = await zip.generateAsync({ type: 'blob' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = zipName;
  a.click();
  URL.revokeObjectURL(a.href);

  toast.success(
    failed > 0
      ? `Downloaded ${succeeded} photo${succeeded !== 1 ? 's' : ''} (${failed} failed)`
      : `Downloaded ${succeeded} photo${succeeded !== 1 ? 's' : ''}`,
    { id: toastId },
  );
}

// ── Collapsible album group ───────────────────────────────────────────────────

interface AlbumGroupSectionProps {
  albumId: string;
  albumName: string;
  totalCount: number;
  load: AlbumLoad;
  /** Global flat viewer array; this album's photos start at `startIndex`. */
  viewerPhotos: Photo[];
  startIndex: number;
  /** Load the next page for this album (first page or "load more"). */
  onLoad: (albumId: string) => void;
  onView: (globalIndex: number) => void;
  /** Fetch every matched photo for this album (used for the per-album download). */
  fetchAll: (albumId: string) => Promise<MyPhoto[]>;
}

function AlbumGroupSection({
  albumId,
  albumName,
  totalCount,
  load,
  viewerPhotos,
  startIndex,
  onLoad,
  onView,
  fetchAll,
}: AlbumGroupSectionProps) {
  const [collapsed, setCollapsed] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const rootRef = useRef<HTMLDivElement>(null);
  const moreSentinelRef = useRef<HTMLDivElement>(null);

  // First page: load when the group scrolls into view (unless collapsed).
  useEffect(() => {
    if (collapsed || load.loaded || load.loading) return;
    const el = rootRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => { if (entries[0].isIntersecting) onLoad(albumId); },
      { rootMargin: '300px' },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [collapsed, load.loaded, load.loading, albumId, onLoad]);

  // Subsequent pages: load when the bottom sentinel scrolls into view.
  useEffect(() => {
    if (collapsed || !load.loaded || !load.hasMore || load.loading) return;
    const el = moreSentinelRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => { if (entries[0].isIntersecting) onLoad(albumId); },
      { rootMargin: '400px' },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [collapsed, load.loaded, load.hasMore, load.loading, albumId, onLoad]);

  const handleDownload = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (downloading) return;
    setDownloading(true);
    const toastId = `album-download-${albumId}`;
    try {
      const all = await fetchAll(albumId);
      if (all.length === 0) {
        toast.error('No photos to download.', { id: toastId });
        return;
      }

      // Client-side archiver — streams to disk with real progress in the
      // DownloadPanel, so no toast and no in-memory ceiling.
      if (CLIENT_ZIP_ENABLED) {
        await startClientZip({
          target: { kind: 'album', id: albumId },
          albumName,
          scope: 'my-photos',
          photoIds: all.map((p) => p._id),
        });
        return;
      }

      toast.loading(`Preparing photos from "${albumName}"…`, { id: toastId });
      const folderName = albumName.replace(/\s+/g, '-').toLowerCase();
      await downloadPhotosAsZip(all, folderName, `${folderName}.zip`, toastId);
    } catch {
      toast.error('Download failed. Please try again.', { id: toastId });
    } finally {
      setDownloading(false);
    }
  };

  const displayCount = totalCount || load.photos.length;
  // How many placeholder skeletons to show before the first page arrives.
  const skeletonCount = Math.min(Math.max(displayCount, 4), 8);

  return (
    <div className={styles['group']} ref={rootRef}>
      <div
        className={styles['group-header']}
        onClick={() => setCollapsed((c) => !c)}
        role="button"
        aria-expanded={!collapsed}
        tabIndex={0}
        onKeyDown={(e) => e.key === 'Enter' && setCollapsed((c) => !c)}
      >
        <span className={styles['group-accent']} />
        <span className={styles['group-name']}>{albumName}</span>
        <button
          className={styles['group-download-btn']}
          onClick={handleDownload}
          disabled={downloading}
          aria-label={`Download all photos from ${albumName}`}
          title={`Download ${albumName}`}
        >
          {downloading
            ? <Loader2 size={13} className={styles['spin']} />
            : <Download size={13} />}
        </button>
        <span className={styles['group-count']}>{displayCount} photo{displayCount !== 1 ? 's' : ''}</span>
        <ChevronDown
          className={[styles['group-chevron'], collapsed ? styles['group-chevron-collapsed'] : ''].join(' ')}
        />
      </div>

      {/* Keep content mounted so photo cells never remount on collapse/expand —
          this prevents the shimmer skeleton from reappearing after a collapse. */}
      <motion.div
        className={styles['group-body']}
        initial={false}
        animate={collapsed ? { height: 0, opacity: 0 } : { height: 'auto', opacity: 1 }}
        transition={{ duration: 0.22, ease: [0.25, 0.46, 0.45, 0.94] }}
      >
        {load.photos.length === 0 ? (
          // Before the first page arrives, show skeletons (the visible group is
          // what triggers the on-demand load above).
          <PhotoGrid className={styles['skeleton-grid']}>
            {Array.from({ length: skeletonCount }).map((_, i) => (
              <PhotoCardSkeleton key={i} />
            ))}
          </PhotoGrid>
        ) : (
          <>
            <PhotoGrid>
              {load.photos.map((photo, i) => (
                <AlbumPhotoCell
                  key={photo._id}
                  photo={viewerPhotos[startIndex + i]}
                  photoIndex={startIndex + i}
                  isSelected={false}
                  selectMode={false}
                  onSelect={() => {}}
                  onView={onView}
                  useClientCompositor={false}
                />
              ))}
            </PhotoGrid>
            {load.hasMore && <div ref={moreSentinelRef} className={styles['sentinel']} />}
            {load.loading && (
              <PhotoGrid className={styles['skeleton-grid']}>
                {Array.from({ length: 4 }).map((_, i) => (
                  <PhotoCardSkeleton key={i} />
                ))}
              </PhotoGrid>
            )}
          </>
        )}
      </motion.div>
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────

export function MyPhotosTab() {
  const { user } = useAuthStore();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastRefreshedAt, setLastRefreshedAt] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [rateLimitMsg, setRateLimitMsg] = useState<string | null>(null);
  const [viewerIndex, setViewerIndex] = useState<number | null>(null);
  const [downloading, setDownloading] = useState(false);
  const [albumTotalCounts, setAlbumTotalCounts] = useState<Record<string, number>>({});
  const [albumNames, setAlbumNames] = useState<Record<string, string>>({});
  const [unscannedCount, setUnscannedCount] = useState(0);
  const [sharePhoto, setSharePhoto] = useState<MyPhoto | null>(null);
  const [reportPhoto, setReportPhoto] = useState<{ albumId: string; photoId: string } | null>(null);

  // Per-album loaded photos, keyed by albumId.
  const [albumData, setAlbumData] = useState<Record<string, AlbumLoad>>({});
  const albumDataRef = useRef(albumData);
  useEffect(() => { albumDataRef.current = albumData; }, [albumData]);
  const inFlightRef = useRef<Set<string>>(new Set());

  // ── Metadata (album list + counts) ───────────────────────────────────────────
  const loadMetadata = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // limit=1 — we only need the album list / counts here; each group fetches
      // its own photos on demand.
      const page = await meApi.getMyPhotos(undefined, 1);
      setLastRefreshedAt(page.lastRefreshedAt);
      setAlbumTotalCounts(page.albumTotalCounts ?? {});
      setAlbumNames(page.albumNames ?? {});
      setUnscannedCount(page.unscannedMembershipsCount ?? 0);
    } catch {
      setError('Could not load your photos. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadMetadata();
  }, [loadMetadata]);

  // ── Per-album loading ────────────────────────────────────────────────────────
  const loadAlbumPage = useCallback(async (albumId: string) => {
    if (inFlightRef.current.has(albumId)) return;
    const cur = albumDataRef.current[albumId] ?? EMPTY_LOAD;
    if (cur.loaded && !cur.hasMore) return;

    inFlightRef.current.add(albumId);
    setAlbumData((d) => ({ ...d, [albumId]: { ...(d[albumId] ?? EMPTY_LOAD), loading: true } }));
    try {
      const page = await meApi.getMyAlbumPhotos(albumId, cur.cursor ?? undefined);
      setAlbumData((d) => {
        const prev = d[albumId] ?? EMPTY_LOAD;
        const seen = new Set(prev.photos.map((p) => p._id));
        const merged = [...prev.photos, ...page.photos.filter((p) => !seen.has(p._id))];
        return {
          ...d,
          [albumId]: {
            photos: merged,
            cursor: page.nextCursor,
            hasMore: page.hasMore,
            loading: false,
            loaded: true,
          },
        };
      });
    } catch {
      setAlbumData((d) => ({ ...d, [albumId]: { ...(d[albumId] ?? EMPTY_LOAD), loading: false } }));
    } finally {
      inFlightRef.current.delete(albumId);
    }
  }, []);

  /** Fetch every matched photo for an album (all pages), deduped by id. */
  const fetchAllAlbumPhotos = useCallback(async (albumId: string): Promise<MyPhoto[]> => {
    const out: MyPhoto[] = [];
    const seen = new Set<string>();
    let cursor: string | undefined;
    for (let i = 0; i < 200; i++) {
      const page = await meApi.getMyAlbumPhotos(albumId, cursor);
      for (const p of page.photos) {
        if (!seen.has(p._id)) { seen.add(p._id); out.push(p); }
      }
      if (!page.hasMore || !page.nextCursor) break;
      cursor = page.nextCursor;
    }
    return out;
  }, []);

  // Albums in display order: biggest first, then alphabetical.
  const orderedAlbumIds = useMemo(
    () =>
      Object.keys(albumTotalCounts)
        .filter((id) => (albumTotalCounts[id] ?? 0) > 0)
        .sort(
          (a, b) =>
            (albumTotalCounts[b] - albumTotalCounts[a]) ||
            (albumNames[a] ?? '').localeCompare(albumNames[b] ?? ''),
        ),
    [albumTotalCounts, albumNames],
  );

  // ── Refresh ───────────────────────────────────────────────────────────────
  const handleRefresh = async () => {
    if (refreshing) return;
    setRateLimitMsg(null);
    setRefreshing(true);
    try {
      await meApi.refreshMyPhotos();
      // Reset loaded photos so each group re-fetches with fresh matches.
      inFlightRef.current.clear();
      setAlbumData({});
      await loadMetadata();
    } catch (err: unknown) {
      const axiosErr = err as { response?: { status?: number; data?: { retryAfter?: number } } };
      if (axiosErr?.response?.status === 429) {
        const retryAfter = axiosErr.response?.data?.retryAfter ?? 60;
        setRateLimitMsg(`Rate limited — try again in ${retryAfter}s`);
      }
    } finally {
      setRefreshing(false);
    }
  };

  // ── Download all ───────────────────────────────────────────────────────────
  const handleDownloadAll = async () => {
    if (downloading || orderedAlbumIds.length === 0) return;
    setDownloading(true);
    const toastId = 'my-photos-download';
    toast.loading('Preparing your photos for download…', { id: toastId });
    try {
      const all: MyPhoto[] = [];
      const seen = new Set<string>();
      for (const albumId of orderedAlbumIds) {
        const ps = await fetchAllAlbumPhotos(albumId);
        for (const p of ps) {
          if (!seen.has(p._id)) { seen.add(p._id); all.push(p); }
        }
      }
      if (all.length === 0) {
        toast.error('No photos to download.', { id: toastId });
        return;
      }
      await downloadPhotosAsZip(all, 'my-pixel-machha-photos', 'my-pixel-machha-photos.zip', toastId);
    } catch {
      toast.error('Download failed. Please try again.', { id: toastId });
    } finally {
      setDownloading(false);
    }
  };

  // ── Share / Report ─────────────────────────────────────────────────────────

  const findLoadedPhoto = useCallback((photoId: string): MyPhoto | null => {
    for (const id of Object.keys(albumDataRef.current)) {
      const found = albumDataRef.current[id].photos.find((p) => p._id === photoId);
      if (found) return found;
    }
    return null;
  }, []);

  const handleShare = useCallback((photoId: string) => {
    const photo = findLoadedPhoto(photoId);
    if (photo) setSharePhoto(photo);
  }, [findLoadedPhoto]);

  const handleReport = useCallback((photoId: string) => {
    const photo = findLoadedPhoto(photoId);
    if (photo) setReportPhoto({ albumId: photo.albumId, photoId: photo._id });
  }, [findLoadedPhoto]);

  // ── States ─────────────────────────────────────────────────────────────────

  if (!user?.facePhotoKey) {
    return (
      <div className={styles['empty-wrap']}>
        <MachhaCharacter state="Onboarding" size={160} />
        <h2 className={styles['empty-title']}>Find your photos</h2>
        <p className={styles['empty-desc']}>
          Upload a selfie to find your photos across all your albums
        </p>
        <Button variant="primary" size="md" onClick={() => navigate('/albums')}>
          Upload Selfie
        </Button>
      </div>
    );
  }

  if (loading) {
    return (
      <div>
        {/* toolbar skeleton */}
        <div className={styles['skeleton-toolbar']}>
          <Skeleton style={{ width: '9rem', height: '0.875rem', borderRadius: 6 }} />
        </div>
        {/* two fake album groups */}
        {[8, 6].map((count, gi) => (
          <div key={gi} className={styles['group']}>
            <div className={styles['skeleton-group-header']}>
              <Skeleton style={{ width: 3, height: '1.25rem', borderRadius: 2 }} />
              <Skeleton style={{ width: '8rem', height: '0.9375rem', borderRadius: 6 }} />
              <Skeleton style={{ width: '2rem', height: '2rem', borderRadius: 'var(--radius-clay-sm)', flexShrink: 0 }} />
              <Skeleton style={{ width: '4rem', height: '0.75rem', borderRadius: 6, marginLeft: 'auto' }} />
            </div>
            <PhotoGrid className={styles['skeleton-grid']}>
              {Array.from({ length: count }).map((_, i) => (
                <PhotoCardSkeleton key={i} />
              ))}
            </PhotoGrid>
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles['empty-wrap']}>
        <MachhaCharacter state="Error" size={140} />
        <h2 className={styles['empty-title']}>Something went wrong</h2>
        <p className={styles['empty-desc']}>{error}</p>
        <Button variant="primary" size="md" onClick={loadMetadata}>Retry</Button>
      </div>
    );
  }

  if (orderedAlbumIds.length === 0) {
    return (
      <div className={styles['empty-wrap']}>
        <MachhaCharacter state="Empty" size={160} />
        <h2 className={styles['empty-title']}>No photos found yet</h2>
        <p className={styles['empty-desc']}>
          Try refreshing — face matching may still be processing.
        </p>
        {rateLimitMsg && <p className={styles['rate-limit-msg']}>{rateLimitMsg}</p>}
        <Button
          variant="primary"
          size="md"
          onClick={handleRefresh}
          loading={refreshing}
          disabled={refreshing}
        >
          {refreshing ? 'Searching…' : 'Refresh'}
        </Button>
      </div>
    );
  }

  // ── Loaded state ───────────────────────────────────────────────────────────

  // Build the flat viewer array + per-group offsets from currently-loaded photos.
  const flatPhotos: MyPhoto[] = [];
  const groupViews = orderedAlbumIds.map((albumId) => {
    const load = albumData[albumId] ?? EMPTY_LOAD;
    const startIndex = flatPhotos.length;
    flatPhotos.push(...load.photos);
    return { albumId, startIndex, load };
  });
  const viewerPhotos = flatPhotos.map(myPhotoToViewerPhoto);

  return (
    <div>
      {/* "Last updated" info row */}
      <div className={styles['toolbar']}>
        <span className={styles['refresh-label']}>
          Last updated {formatRelativeTime(lastRefreshedAt)}
        </span>
        {rateLimitMsg && <span className={styles['rate-limit-msg']}>{rateLimitMsg}</span>}
      </div>

      {/* Unscanned events notice */}
      {unscannedCount > 0 && (
        <div className={styles['unscanned-banner']}>
          {unscannedCount} event{unscannedCount !== 1 ? 's' : ''} haven't been scanned yet.
          Tap <strong>Refresh</strong> to find all your photos.
        </div>
      )}

      {/* Grouped photo grids — each loads its own photos on demand */}
      {groupViews.map(({ albumId, startIndex, load }) => (
        <AlbumGroupSection
          key={albumId}
          albumId={albumId}
          albumName={albumNames[albumId] ?? 'Unknown Album'}
          totalCount={albumTotalCounts[albumId] ?? 0}
          load={load}
          viewerPhotos={viewerPhotos}
          startIndex={startIndex}
          onLoad={loadAlbumPage}
          onView={setViewerIndex}
          fetchAll={fetchAllAlbumPhotos}
        />
      ))}

      <div className={styles['page-bottom-pad']} />

      {/* Sticky bar — download + refresh */}
      <div className={styles['sticky-bar']}>
        <button
          className={styles['download-btn']}
          onClick={handleDownloadAll}
          disabled={downloading}
        >
          {downloading
            ? <Loader2 size={15} className={styles.spinner} />
            : <Download size={15} />}
          {downloading ? 'Downloading…' : 'Download All'}
        </button>
        <button
          className={styles['refresh-btn']}
          onClick={handleRefresh}
          disabled={refreshing}
        >
          <RefreshCw size={15} className={refreshing ? styles.spinner : undefined} />
          {refreshing ? 'Searching…' : 'Refresh'}
        </button>
      </div>

      {/* Photo lightbox */}
      {viewerIndex !== null && (
        <AlbumPhotoViewer
          photos={viewerPhotos}
          initialIndex={viewerIndex}
          onClose={() => setViewerIndex(null)}
          onShare={handleShare}
          onReport={handleReport}
          useClientCompositor={false}
        />
      )}

      {/* Share sheet */}
      {sharePhoto && (
        <ShareSheet
          isOpen
          onClose={() => setSharePhoto(null)}
          photoUrl={sharePhoto.processedUrl || sharePhoto.originalUrl || ''}
          albumName={sharePhoto.albumName}
          eventName=""
        />
      )}

      {/* Report sheet */}
      {reportPhoto && (
        <PhotoReportSheet
          isOpen={!!reportPhoto}
          onClose={() => setReportPhoto(null)}
          albumId={reportPhoto.albumId}
          photoId={reportPhoto.photoId}
          onReported={() => setReportPhoto(null)}
        />
      )}
    </div>
  );
}
